import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageCircle, Heart, MoreVertical, Pin } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CommunityPost } from '@/types/community';
import { useRole } from '@/hooks/useRole';
import { Card, CardContent } from '@/components/ui/card';
import PostComments from './PostComments';
import { togglePostLike, fetchUserPostInteractions } from '@/services/communityPostService';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';

interface PostCardProps {
  post: CommunityPost;
  onEditPost?: (post: CommunityPost) => void;
  onDeletePost?: (postId: string) => void;
  isDeleting?: boolean;
  onInteraction?: () => void; // Callback para atualizações de ranking
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  onEditPost,
  onDeletePost,
  isDeleting = false,
  onInteraction
}) => {
  const { user } = useRole();
  const { toast } = useToast();
  const [showComments, setShowComments] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  
  const formattedDate = formatDistanceToNow(new Date(post.created_at), {
    addSuffix: true,
    locale: ptBR
  });

  const isAuthor = user?.id === post.author_id;
  const canEdit = isAuthor; // Pode expandir para incluir moderadores
  const canDelete = isAuthor; // Pode expandir para incluir moderadores

  // Buscar status de like e contagem quando componente carrega
  useEffect(() => {
    const fetchLikeStatusAndCount = async () => {
      try {
        console.log('🔄 [PostCard] Iniciando busca de likes para post:', post.id);
        console.log('🔄 [PostCard] Usuário atual:', user?.id);
        
        // Buscar contagem atual de likes do banco
        const { data: likesData, error: likesError } = await supabase
          .from('community_post_likes')
          .select('id, user_id, created_at')
          .eq('post_id', post.id);
          
        console.log('📋 [PostCard] Resultado da consulta de likes:', { likesData, likesError });
          
        if (!likesError && likesData) {
          const currentLikesCount = likesData.length;
          console.log('📊 [PostCard] Contagem inicial de likes:', currentLikesCount);
          console.log('📋 [PostCard] Lista completa de likes:', likesData);
          setLikesCount(currentLikesCount);
          
          // Verificar se o usuário atual curtiu o post
          if (user) {
            const userLike = likesData.find(like => like.user_id === user.id);
            const userLiked = !!userLike;
            console.log('🔍 [PostCard] Like do usuário encontrado:', userLike);
            console.log('🔍 [PostCard] Status de like do usuário:', userLiked);
            setIsLiked(userLiked);
          } else {
            console.log('⚠️ [PostCard] Usuário não autenticado, setando like como false');
            setIsLiked(false);
          }
        } else {
          console.warn('⚠️ [PostCard] Erro ao buscar likes:', likesError);
          setLikesCount(0);
          setIsLiked(false);
        }
      } catch (error) {
        console.error('❌ [PostCard] Erro ao buscar status de like:', error);
        setLikesCount(0);
        setIsLiked(false);
      }
    };

    fetchLikeStatusAndCount();
  }, [post.id, user]);

  const handleLike = async () => {
    if (!user || isLiking) return;

    setIsLiking(true);
    
    try {
      console.log('❤️ [PostCard] Iniciando toggle like para post:', post.id);
      console.log('👤 [PostCard] User ID:', user.id);
      
      const newLikedState = await togglePostLike(post.id, user.id);
      console.log('❤️ [PostCard] Novo estado do like:', newLikedState);
      
      // Atualizar estado imediatamente baseado no resultado da função
      setIsLiked(newLikedState);
      
      // Aguardar um delay menor para a transação
      console.log('⏳ [PostCard] Aguardando transação...'); 
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Atualizar contagem baseada no estado
      setLikesCount(prev => {
        const newCount = newLikedState ? prev + 1 : Math.max(0, prev - 1);
        console.log('📊 [PostCard] Nova contagem de likes:', newCount);
        return newCount;
      });
      
      // Verificação simples para debug (não afeta o estado)
      const { data: debugLikes } = await supabase
        .from('community_post_likes')
        .select('*')
        .eq('post_id', post.id);
      
      console.log('🔍 [PostCard] Debug - Total likes no banco:', debugLikes?.length || 0);
      console.log('🔍 [PostCard] Debug - Estado local:', { liked: newLikedState, count: newLikedState ? likesCount + 1 : Math.max(0, likesCount - 1) });
      
      toast({
        title: newLikedState ? "Post curtido!" : "Curtida removida",
        description: newLikedState ? "Você curtiu este post" : "Você removeu a curtida",
      });

      // Notificar sobre interação para atualizar ranking
      if (onInteraction) {
        console.log('🔄 [PostCard] Notificando interação para atualizar ranking');
        onInteraction();
      }
      
    } catch (error) {
      console.error('❌ [PostCard] Erro ao curtir post:', error);
      toast({
        title: "Erro",
        description: "Não foi possível curtir o post",
        variant: "destructive",
      });
    } finally {
      setIsLiking(false);
    }
  };

  const handleEditClick = () => {
    if (onEditPost) {
      onEditPost(post);
    }
  };

  const handleDeleteClick = () => {
    if (onDeletePost && window.confirm('Tem certeza que deseja deletar este post?')) {
      onDeletePost(post.id);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-[#1A2332] to-[#1E2A3A] border-[#2A3749] hover:border-[#3A4A60] transition-all duration-300 shadow-lg hover:shadow-xl">
      <CardContent className="p-0">
        {/* Header com título destacado */}
        {post.title && (
          <div className="relative overflow-hidden">
            {/* Fundo gradiente para o título */}
            <div className="bg-gradient-to-r from-[#00B6F0]/10 via-[#9f00ff]/5 to-[#00B6F0]/10 p-6 border-b border-[#2A3749]">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {post.is_pinned && (
                      <Badge className="bg-[#9f00ff]/20 text-[#9f00ff] border-[#9f00ff]/30 text-xs">
                        <Pin className="h-3 w-3 mr-1" />
                        Fixado
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide leading-tight">
                    {post.title}
                  </h3>
                </div>
                
                {/* Menu de ações */}
                {(canEdit || canDelete) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-[#2A3749] rounded-full"
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="end" 
                      className="bg-[#1A2332] border-[#2A3749] z-[9999] min-w-[120px]"
                    >
                      {canEdit && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditClick();
                          }}
                          className="text-gray-300 hover:text-white hover:bg-[#2A3749] cursor-pointer"
                        >
                          Editar
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteClick();
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                          disabled={isDeleting}
                        >
                          {isDeleting ? 'Deletando...' : 'Deletar'}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="p-6">
          {/* Informações do autor */}
          <div className="flex items-center gap-3 mb-4">
            <Avatar className="h-10 w-10 border-2 border-[#00B6F0]/30">
              <AvatarImage 
                src={post.author?.avatar_url} 
                alt={post.author?.nickname || 'Usuário'}
              />
              <AvatarFallback className="bg-[#00B6F0] text-black text-sm font-medium">
                {post.author?.nickname?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="text-white font-medium text-sm">
                {post.author?.nickname || 'Usuário Desconhecido'}
              </div>
              <div className="text-gray-400 text-xs">
                {formattedDate}
              </div>
            </div>
          </div>

          {/* Conteúdo do post */}
          <div className="mb-4">
            <div 
              className="text-gray-300 leading-relaxed prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* Imagem do post */}
          {post.media_url && (
            <div className="mb-4 rounded-lg overflow-hidden">
              <img 
                src={post.media_url} 
                alt="Conteúdo do post"
                className="w-full max-h-96 object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Botões de interação */}
          <div className="flex items-center gap-4 pt-4 border-t border-[#2A3749]">
            <Button
              variant="ghost"
              size="sm"
              className={`transition-colors gap-2 ${
                isLiked 
                  ? 'text-red-400 hover:text-red-300 bg-red-400/10' 
                  : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
              }`}
              type="button"
              onClick={handleLike}
              disabled={isLiking}
            >
              <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
              <span className="text-sm">{likesCount}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-[#00B6F0] hover:bg-[#00B6F0]/10 transition-colors gap-2"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowComments(!showComments);
              }}
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-sm">{post.comments_count || 0} comentários</span>
            </Button>
          </div>

          {/* Seção de comentários */}
          {showComments && (
            <div className="mt-6 pt-6 border-t border-[#2A3749]">
              <PostComments 
                postId={post.id} 
                onCommentCreated={onInteraction}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PostCard; 